/**
 * Issuing and revoking the credentials agents report with.
 *
 * The key is displayed once, in the response that created it, and then it is
 * gone — DCC keeps only its hash. That is stated on screen rather than buried
 * in documentation, because there is no support call that can recover it.
 */
import { useCallback, useState } from 'react';
import { api, ApiRequestError, type IssuedAgentKey } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { Panel, ToneChip } from './Primitives';
import { EmptyState, ErrorState, SkeletonPanel } from './States';
import { relativeTime } from '../lib/format';

export function AgentKeysPanel({ projects }: { projects: { id: string; name: string }[] }) {
  const keys = useProjection(() => api.agentKeys(), []);
  const [label, setLabel] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [issued, setIssued] = useState<IssuedAgentKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const issue = useCallback(async () => {
    if (!label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.issueAgentKey(label.trim(), projectId || null);
      setIssued(response.data);
      setLabel('');
      keys.reload();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'The key could not be issued.');
    } finally {
      setBusy(false);
    }
  }, [label, projectId, keys]);

  const revoke = useCallback(
    async (keyId: string) => {
      setError(null);
      try {
        await api.revokeAgentKey(keyId, 'Revoked from the integrations screen.');
        keys.reload();
      } catch (err) {
        setError(err instanceof ApiRequestError ? err.message : 'The key could not be revoked.');
      }
    },
    [keys],
  );

  if (keys.loading) return <SkeletonPanel rows={3} />;
  if (keys.error) return <ErrorState error={keys.error} onRetry={keys.reload} />;

  const rows = keys.data!.keys;

  return (
    <Panel
      title="Agent credentials"
      subtitle="What an agent authenticates with when it reports its own work. A key bound to one project cannot report about another."
    >
      <div className="stack">
        <div className="inline" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="btn btn--sm"
            style={{ minWidth: 220 }}
            placeholder="What runs with this key"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            aria-label="Agent key label"
          />
          <select
            className="btn btn--sm"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            aria-label="Project this key may report about"
          >
            <option value="">Every project in the tenant</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={busy || label.trim().length === 0}
            onClick={() => void issue()}
          >
            Issue a key
          </button>
        </div>

        {error && (
          <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
            {error}
          </p>
        )}

        {issued && (
          <div className="panel" data-tone="attention" style={{ padding: 12 }}>
            <div className="label">{issued.notice}</div>
            <p className="mono small" style={{ wordBreak: 'break-all', margin: '8px 0' }}>
              {issued.key}
            </p>
            <button type="button" className="btn btn--sm" onClick={() => setIssued(null)}>
              I have stored it
            </button>
          </div>
        )}

        {rows.length === 0 ? (
          <EmptyState
            tone="neutral"
            title="No agent has been given a credential yet"
            body="Until a key exists, nothing can report progress, blockers or decisions into this tenant."
          />
        ) : (
          <div className="rows">
            {rows.map((key) => (
              <div key={key.id} className="row" style={{ gridTemplateColumns: '1fr auto' }}>
                <div className="row__stack row__cell" data-label="Key">
                  <span className="inline inline--tight">
                    <ToneChip tone={key.revokedAt ? 'neutral' : 'active'}>
                      {key.revokedAt ? 'REVOKED' : 'ACTIVE'}
                    </ToneChip>
                    <strong>{key.label}</strong>
                  </span>
                  <span className="row__sub mono truncate">
                    {key.prefix}… ·{' '}
                    {key.projectId
                      ? (projects.find((p) => p.id === key.projectId)?.name ?? key.projectId)
                      : 'every project'}
                  </span>
                  <span className="row__sub">
                    {key.lastUsedAt
                      ? `last used ${relativeTime(key.lastUsedAt)} · ${key.useCount} calls`
                      : 'never used'}
                    {key.revokedReason ? ` · ${key.revokedReason}` : ''}
                  </span>
                </div>
                <div className="row__cell" data-label="Action">
                  {key.revokedAt ? (
                    <span className="tiny dim">revoked {relativeTime(key.revokedAt)}</span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => void revoke(key.id)}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
